import { cleanMentionText } from '@common/editor-layer/utils/mention-renderer'
import { mergeAttributes, Node, type ChainedCommands } from '@tiptap/core'
import { VueNodeViewRenderer } from '@tiptap/vue-3'
import EditableMentionNode from './EditableMentionNode.vue'

export interface UnifiedMentionAttrs {
  id?: string
  label?: string
  name?: string
  slug?: string
  icon?: string
  iconType?: 'emoji' | 'icon'
  timestamp?: number
  type?: 'channel' | 'event' | 'user' | 'course' | 'broadcast'
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    unifiedMention: {
      insertMention: (attrs: UnifiedMentionAttrs) => ReturnType
    }
  }
}

export const getMentionLabelFromElement = (element: Element): string => {
  const explicitLabel = element.getAttribute('data-label')
  if (explicitLabel) return explicitLabel

  const name = element.getAttribute('data-name')
  if (name) return name

  const mentionTextNode = (element as HTMLElement).querySelector('.mention-text')
  const copiedText = mentionTextNode?.textContent || element.textContent || ''
  return cleanMentionText(copiedText)
}

export const UnifiedMention = Node.create({
  name: 'unifiedMention',
  group: 'inline',
  inline: true,
  atom: true,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: element => element.getAttribute('data-id'),
        renderHTML: attributes => (attributes.id ? { 'data-id': attributes.id } : {}),
      },
      label: {
        default: '',
        parseHTML: element => element.getAttribute('data-label'),
        renderHTML: attributes => (attributes.label ? { 'data-label': attributes.label } : {}),
      },
      name: {
        default: '',
        parseHTML: element => element.getAttribute('data-name'),
        renderHTML: attributes => (attributes.name ? { 'data-name': attributes.name } : {}),
      },
      slug: {
        default: '',
        parseHTML: element => element.getAttribute('data-slug'),
        renderHTML: attributes => (attributes.slug ? { 'data-slug': attributes.slug } : {}),
      },
      icon: {
        default: '',
        parseHTML: element => element.getAttribute('data-icon'),
        renderHTML: attributes => (attributes.icon ? { 'data-icon': attributes.icon } : {}),
      },
      iconType: {
        default: 'emoji',
        parseHTML: element => element.getAttribute('data-icon-type'),
        renderHTML: attributes => (attributes.iconType ? { 'data-icon-type': attributes.iconType } : {}),
      },
      timestamp: {
        default: null,
        parseHTML: element => {
          const timestamp = element.getAttribute('data-timestamp')
          return timestamp ? parseInt(timestamp, 10) : null
        },
        renderHTML: attributes => (attributes.timestamp ? { 'data-timestamp': attributes.timestamp } : {}),
      },
      type: {
        default: 'channel',
        parseHTML: element => {
          // Prefer internal mention type when available
          const internal = element.getAttribute('data-mention-type')
          if (internal) return internal

          // Fallback: map external data-type to internal type
          const external = element.getAttribute('data-type')
          switch (external) {
            case 'channel-mention':
              return 'channel'
            case 'events-mention':
              return 'event'
            case 'courses-mention':
              return 'course'
            case 'mention':
              return 'user'
            default:
              return 'channel'
          }
        },
        renderHTML: attributes => {
          // Only emit data-mention-type for user/broadcast like the preview app
          if (attributes.type === 'user' || attributes.type === 'broadcast') {
            return { 'data-mention-type': attributes.type }
          }
          return {}
        },
      },
    }
  },

  parseHTML() {
    return [
      // Parse original mention types as <a> tags (primary format)
      {
        tag: 'a[data-type="channel-mention"]',
        priority: 10001,
        getAttrs: element => ({
          type: 'channel',
          id: element.getAttribute('data-id'),
          label: getMentionLabelFromElement(element),
          name: element.getAttribute('data-name'),
          slug: element.getAttribute('data-slug'),
          icon: element.getAttribute('data-icon'),
          iconType: element.getAttribute('data-icon-type'),
        }),
      },
      {
        tag: 'a[data-type="events-mention"]',
        priority: 10001,
        getAttrs: element => ({
          type: 'event',
          id: element.getAttribute('data-id'),
          label: getMentionLabelFromElement(element),
          timestamp: element.getAttribute('data-timestamp'),
        }),
      },
      {
        tag: 'a[data-type="courses-mention"]',
        priority: 10001,
        getAttrs: element => ({
          type: 'course',
          id: element.getAttribute('data-id'),
          label: getMentionLabelFromElement(element),
          name: element.getAttribute('data-name'),
        }),
      },
      {
        tag: 'a[data-type="mention"]',
        priority: 10001,
        getAttrs: element => ({
          type: 'user',
          id: element.getAttribute('data-id'),
          label: getMentionLabelFromElement(element),
          name: element.getAttribute('data-name'),
          slug: element.getAttribute('data-slug'),
        }),
      },
      // Parse broadcast mentions as spans
      {
        tag: 'span[data-type="mention"]',
        priority: 10001,
        getAttrs: element => {
          const mentionType = element.getAttribute('data-mention-type')
          if (mentionType === 'broadcast') {
            return {
              type: 'broadcast',
              label: getMentionLabelFromElement(element),
              name: element.getAttribute('data-name'),
            }
          }
          return false
        },
      },
      // Also parse as span for any legacy content
      {
        tag: 'span[data-type="channel-mention"]',
        priority: 10001,
        getAttrs: element => ({
          type: 'channel',
          id: element.getAttribute('data-id'),
          label: getMentionLabelFromElement(element),
          name: element.getAttribute('data-name'),
          slug: element.getAttribute('data-slug'),
          icon: element.getAttribute('data-icon'),
          iconType: element.getAttribute('data-icon-type'),
        }),
      },
      {
        tag: 'span[data-type="events-mention"]',
        priority: 10001,
        getAttrs: element => ({
          type: 'event',
          id: element.getAttribute('data-id'),
          label: getMentionLabelFromElement(element),
          timestamp: element.getAttribute('data-timestamp'),
        }),
      },
    ]
  },

  renderHTML({ HTMLAttributes, node }) {
    // Map internal type to the original data-type values
    const getDataType = (type: string) => {
      switch (type) {
        case 'channel':
          return 'channel-mention'
        case 'event':
          return 'events-mention'
        case 'course':
          return 'courses-mention'
        case 'user':
        case 'broadcast':
          return 'mention'
        default:
          return 'channel-mention'
      }
    }

    // Build attributes (styled by global CSS)
    const baseAttrs: Record<string, string> = {
      'data-type': getDataType(node.attrs.type),
      contenteditable: 'false',
    }

    // Only add data-mention-type for user/broadcast (parity with preview app)
    if (node.attrs.type === 'user' || node.attrs.type === 'broadcast') {
      baseAttrs['data-mention-type'] = node.attrs.type
    }

    // Do not emit a placeholder href in the serialized content. The real
    // navigation href is added at display time by the mention renderer
    // (see mention-renderer.ts). Persisting href="#" pollutes saved content.

    // Add all available attributes
    if (node.attrs.id) baseAttrs['data-id'] = node.attrs.id
    if (node.attrs.label) baseAttrs['data-label'] = node.attrs.label
    if (node.attrs.name) baseAttrs['data-name'] = node.attrs.name
    if (node.attrs.slug) baseAttrs['data-slug'] = node.attrs.slug
    if (node.attrs.icon) baseAttrs['data-icon'] = node.attrs.icon
    if (node.attrs.iconType) baseAttrs['data-icon-type'] = node.attrs.iconType
    if (node.attrs.timestamp) baseAttrs['data-timestamp'] = node.attrs.timestamp.toString()

    // Stable ids for E2E (channel / event mentions)
    if (node.attrs.type === 'channel' && node.attrs.id) {
      baseAttrs.id = `e2e-channel-mention-${node.attrs.id}`
    } else if (node.attrs.type === 'event' && node.attrs.id) {
      baseAttrs.id = `e2e-event-mention-${node.attrs.id}`
    }

    // For renderHTML, we'll use a simpler approach and let CSS handle the styling
    // The icon rendering will be handled in the EditableMentionNode for editor context
    const displayText = node.attrs.label || node.attrs.name || node.attrs.slug || ''
    const cleanedText = cleanMentionText(displayText)

    // Build display content with appropriate prefix
    let displayContent = ''
    switch (node.attrs.type) {
      case 'user':
        displayContent = `@${cleanedText}`
        break
      case 'broadcast':
        displayContent = `@${cleanedText}`
        break
      case 'event':
        displayContent = `📅${cleanedText}`
        break
      case 'course':
        displayContent = `📺${cleanedText}`
        break
      case 'channel':
      default:
        if (node.attrs.iconType === 'emoji' && node.attrs.icon) {
          displayContent = `${node.attrs.icon}${cleanedText}`
        } else {
          displayContent = `#${cleanedText}`
        }
        break
    }

    // Render broadcast mentions as spans, others as anchors
    const tagName = node.attrs.type === 'broadcast' ? 'span' : 'a'

    return [tagName, mergeAttributes(HTMLAttributes, baseAttrs), displayContent]
  },

  renderText({ node }) {
    // Generate plain text representation (used for copy/paste, etc.)
    const displayName = node.attrs.label || node.attrs.name || node.attrs.slug || ''
    const cleanedText = cleanMentionText(displayName)

    // For plain text, we can include simple prefixes
    switch (node.attrs.type) {
      case 'user':
        return `@${cleanedText}`
      case 'broadcast':
        return `@${cleanedText}`
      case 'event':
        return `📅${cleanedText}`
      case 'course':
        return `📺${cleanedText}`
      case 'channel':
      default:
        if (node.attrs.iconType === 'emoji' && node.attrs.icon) {
          return `${node.attrs.icon}${cleanedText}`
        }
        return `#${cleanedText}`
    }
  },

  addCommands() {
    return {
      insertMention:
        attrs =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs }),
      // Backward compatibility commands
      insertChannelMention:
        (attributes: { id: string; label?: string; name?: string; slug?: string; icon?: string; iconType?: string }) =>
        ({ commands }: { commands: ChainedCommands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: { ...attributes, type: 'channel' },
          })
        },
      insertEventsMention:
        (attributes: { id: string; label: string; timestamp?: number }) =>
        ({ commands }: { commands: ChainedCommands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: { ...attributes, type: 'event' },
          })
        },
    }
  },

  addNodeView() {
    return VueNodeViewRenderer(EditableMentionNode)
  },
})
